import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

export type EntranceCardData = {
  id: string;
  name: string;
  badge: string;
  role: 'primary' | 'secondary' | 'accent';
  url: string;
  displayUrl: string;
  latency: string;
  statusText: string;
  updated: string;
  description: string;
  tags: string[];
  index: number;
};

const ROLE_STYLES = {
  primary: {
    frame: 'bg-background-50 border-primary-200',
    badge: 'bg-primary-500 text-background-50',
    accentBar: 'bg-primary-500',
    icon: 'bg-primary-100 text-primary-700',
    button: 'bg-primary-500 text-background-50 hover:bg-primary-600',
  },
  secondary: {
    frame: 'bg-background-50 border-secondary-300',
    badge: 'bg-secondary-500 text-background-50',
    accentBar: 'bg-secondary-500',
    icon: 'bg-secondary-100 text-secondary-800',
    button: 'bg-secondary-500 text-background-50 hover:bg-secondary-600',
  },
  accent: {
    frame: 'bg-accent-50 border-accent-300',
    badge: 'bg-accent-500 text-background-50',
    accentBar: 'bg-accent-500',
    icon: 'bg-accent-100 text-accent-800',
    button: 'bg-accent-500 text-background-50 hover:bg-accent-600',
  },
} as const;

const ROLE_ICONS: Record<EntranceCardData['role'], string> = {
  primary: 'ri-rocket-2-line',
  secondary: 'ri-swap-box-line',
  accent: 'ri-bookmark-3-line',
};

export default function EntranceCard({ data }: { data: EntranceCardData }) {
  const { copiedId, copy } = useCopyToClipboard();
  const isCopied = copiedId === data.id;
  const styles = ROLE_STYLES[data.role];

  return (
    <article
      className={`relative flex flex-col rounded-lg border ${styles.frame} p-5 md:p-6 h-full animate-fade-up transition-colors duration-300 hover:border-primary-300`}
      style={{ animationDelay: `${data.index * 120}ms` }}
    >
      <span className={`absolute left-0 top-6 bottom-6 w-1 rounded-full ${styles.accentBar}`} aria-hidden="true" />

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`w-11 h-11 flex items-center justify-center rounded-md ${styles.icon} text-xl`}>
            <i className={ROLE_ICONS[data.role]} aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">{data.name}</h3>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs font-label text-foreground-600">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse-dot" aria-hidden="true" />
              {data.statusText} · {data.latency}
            </p>
          </div>
        </div>
        <span className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-label whitespace-nowrap ${styles.badge}`}>
          {data.badge}
        </span>
      </div>

      <p className="mt-4 text-[13px] leading-relaxed text-foreground-700">{data.description}</p>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {data.tags.map((tag) => (
          <span
            key={tag}
            className="px-2 py-0.5 rounded-full bg-background-100 border border-background-200 text-[11px] font-label text-foreground-600 whitespace-nowrap"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-5 px-3 py-2.5 rounded-md bg-background-100 border border-background-200">
        <p className="text-[12px] font-label text-foreground-600 truncate">{data.displayUrl}</p>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => copy(data.url, data.id)}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-label whitespace-nowrap cursor-pointer transition-colors duration-200 ${
            isCopied ? 'bg-accent-500 text-background-50' : styles.button
          }`}
        >
          <i className={isCopied ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden="true" />
          {isCopied ? '已复制，去粘贴打开' : '复制这条入口'}
        </button>
      </div>

      <p className="mt-3 text-[11px] font-label text-foreground-500">{data.updated}</p>
    </article>
  );
}